import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { getDashboardMock } from '../data/dashboardMock';

/**
 * useDashboardData(scope)
 *
 * Carga los datos agregados del Dashboard para un scope dado
 * ('interno' | 'colab').
 *
 * ───────────────────────────────────────────────────────────────────────
 * MODO MOCK (mientras el backend no esté vivo / sin endpoint /dashboard)
 * ───────────────────────────────────────────────────────────────────────
 * Con USE_MOCK = true devuelve los datos de data/dashboardMock.js.
 *
 * EL LUNES, cuando el backend esté levantado y exista el endpoint
 * agregado, basta con poner:
 *
 *     const USE_MOCK = false;
 *
 * El endpoint que el backend debe exponer (pendiente, sub-bloque
 * backend II.5c "Dashboard endpoints"):
 *
 *     GET /api/inventario/dashboard?scope=interno|colab
 *
 *   Respuesta esperada (misma forma que el mock):
 *     {
 *       kpis: { total, asignados, disponibles, en_reparacion,
 *               en_baja, eventos_30d, eventos_desglose },
 *       stock_matrix: { columnas:[{id,nombre}],
 *                        filas:[{slug,nombre,icon,total,
 *                                celdas:[{col_id,cantidad,estado,umbral}]}] },
 *       alertas: [{ tipo, severidad, titulo, detalle, items[] }],
 *       distribucion: [{ slug, nombre, icon, total }],
 *       movimientos: [{ event_type, texto, actor, fecha_iso, fecha_label }]
 *     }
 *
 * Si prefieres no crear un endpoint agregado y componer en cliente,
 * cambia fetchReal() para tirar de /activos + /eventos + /alertas y
 * agregar aquí. Documentado pero NO recomendado (más llamadas).
 * ───────────────────────────────────────────────────────────────────────
 */

const USE_MOCK = false;

// Latencia simulada para que el estado de carga sea visible en mock
const MOCK_DELAY_MS = 350;

async function fetchMock(scope) {
  await new Promise((r) => setTimeout(r, MOCK_DELAY_MS));
  return getDashboardMock(scope);
}

async function fetchReal(scope) {
  // Backend devuelve { data: {...} } siguiendo el patrón del resto del módulo
  const response = await api.get('/dashboard', { scope });
  return response?.data || response;
}

export function useDashboardData(scope) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = USE_MOCK ? await fetchMock(scope) : await fetchReal(scope);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refresh: load, isMock: USE_MOCK };
}
